import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useMenu } from "@/components/shell/Menu";
import type { MenuItemSpec } from "@/components/shell/menuSpec";
import { hasSelectionIn, shouldOpenContextMenu } from "@/lib/contextMenu";
import type { ReadingFolder, ReadingLog } from "@/lib/reading";

import { dropsBeforeX, logLabel } from "./logTabs";

/**
 * The 34px band between the top bar and the log (日本-207).
 *
 * **Never taller, and nothing below it moves.** That is the whole claim the design makes about this
 * feature's cost to the reading screen: one band, one sub-view, one piece of per-log state.
 *
 * **The tab region SCROLLS, and that reverses the spec.** READING-LOGS.md says "Never a horizontal
 * scroller — a hidden scroll in a 700px column is a tab you cannot find", and this file said the
 * same. Alex's call, 5 Aug 2026: tabs fill the space between the library button and `N more ›` and
 * scroll when they exceed it, rather than being capped at four with the rest collapsed away.
 *
 * The original concern is **answered rather than dismissed**, and that is what makes the reversal
 * safe: `N more ›` stays and became a *measured* jump-list of exactly the tabs currently scrolled
 * out of view, so "a tab you cannot find" is now the one state that cannot occur — anything you
 * cannot see is named in that menu, and picking it scrolls it in. The scrollbar itself is hidden,
 * because a visible one inside a 34px band eats a third of it.
 */

/** The MIME type a sentence-row drag travels under, so a tab can tell it from anything else. */
export const SENTENCE_DRAG_TYPE = "application/x-nihongonator-sentence";

/**
 * And the one a TAB drag travels under.
 *
 * Distinct from the sentence type because a tab accepts both and they mean opposite things:
 * dropping a sentence on a tab **moves that sentence into that log**, dropping a tab on a tab
 * **reorders the strip**. One type for both would make the commonest gesture ambiguous.
 */
export const TAB_DRAG_TYPE = "application/x-nihongonator-tab";

/**
 * How long a single click waits to see whether it is really the first half of a double click.
 *
 * Long enough to cover a deliberate double click, short enough that selecting a tab does not feel
 * laggy. The same figure the library's rows use, for the same reason: without it, double-clicking
 * a tab to rename it also selects it on the way through, which is harmless here and is not in the
 * library, so both use one rule rather than two.
 */
const DOUBLE_CLICK_MS = 220;

interface TabStripProps {
  logs: ReadingLog[];
  openLogIds: string[];
  activeLogId: string | null;
  onSelect: (logId: string) => void;
  /**
   * Close a tab. **The log is kept** — this is a view operation, which is exactly why the design
   * refused to call the record a "tab". Only offered for a *named* log; see `onDiscard`.
   */
  onClose: (logId: string) => void;
  /**
   * Discard an unsaved log — the tab AND the record, sentences included.
   *
   * Unnamed logs have no other end: they are never filed, so a closed one would sit in `Unfiled`
   * for ever. Alex's call, 5 Aug 2026. The caller does the confirming, because whether to ask
   * depends on what is in it and the strip has no business knowing that rule.
   */
  onDiscard: (logId: string) => void;
  /** Rename in place. `null` un-names, which is what an emptied field means everywhere else. */
  onRename: (logId: string, name: string | null) => void;
  /** For the tab's right-click `Move to folder ▸`, which is the library's action reached here. */
  folders: ReadingFolder[];
  onFileLog: (logId: string, folderId: string | null) => void;
  onNew: () => void;
  onOpenLibrary: () => void;
  /**
   * Open or close the find band over the open log (日本-603). Beside `+` rather than beside `冊`:
   * `冊` is about the library, this is about the log you are in.
   */
  onToggleFind: () => void;
  /** Lit while the band is open, so the button reads as the thing that closes it too. */
  findOpen: boolean;
  /**
   * Re-arrange the strip. **Local only** — tab order is `openLogIds`, which is per-device state,
   * unlike the library's ordering, which writes `sortOrder` to the server. The two are different
   * things: where a log is *filed*, versus which few you have open here and in what order.
   */
  onMoveTab: (movedId: string, targetId: string, before: boolean) => void;
  /** Dropping a sentence row onto a tab moves it there — the same thing the `⋯` menu does. */
  onDropSentence?: (logId: string, sentenceId: string) => void;
}

export function TabStrip({
  logs,
  openLogIds,
  activeLogId,
  onSelect,
  onClose,
  onDiscard,
  onRename,
  folders,
  onFileLog,
  onNew,
  onOpenLibrary,
  onToggleFind,
  findOpen,
  onMoveTab,
  onDropSentence,
}: TabStripProps) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  /** The tab a dragged tab would land beside, and which side of it. */
  const [tabOver, setTabOver] = useState<{ id: string; before: boolean } | null>(null);
  const [renaming, setRenaming] = useState<{ logId: string; text: string } | null>(null);
  /** Tabs not fully inside the scroll region right now. Measured, never inferred from a count. */
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const overflowRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLElement>());
  /** The pending single-click select, cancelled when a double click arrives. */
  const clickTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
  }, []);

  /**
   * Which tabs are off-screen — measured directly, on every scroll and resize.
   *
   * **Rects rather than an `IntersectionObserver`, and that is a correction rather than a
   * preference.** The observer version asked "is this at least 99% visible", which is the same
   * question at one remove and answers it wrong: sub-pixel layout put a tab a fraction under the
   * threshold while it was plainly on screen, so `1 more ›` appeared with all eight tabs visible
   * and named one you were looking at. A menu that lies about what you cannot see is worse than no
   * menu, because it is the affordance the whole scrolling strip leans on.
   *
   * A tab counts as hidden when either edge falls outside the region by more than a pixel. The
   * tolerance absorbs fractional layout; the *either edge* part is deliberate, because half a name
   * is not something you can pick out of a strip.
   */
  useEffect(() => {
    const region = scrollRef.current;
    if (!region) return;

    const measure = () => {
      const bounds = region.getBoundingClientRect();
      const next = new Set<string>();
      for (const id of openLogIds) {
        const element = tabRefs.current.get(id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.left < bounds.left - 1 || rect.right > bounds.right + 1) next.add(id);
      }
      setHidden((current) =>
        current.size === next.size && [...next].every((id) => current.has(id)) ? current : next,
      );
    };

    measure();
    region.addEventListener("scroll", measure, { passive: true });
    // The region's width changes when the window does AND when `N more` itself appears or goes —
    // which is a feedback loop a plain resize listener would miss. A `ResizeObserver` on the region
    // catches both, and the equality check above is what stops it oscillating.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(region);
    return () => {
      region.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [openLogIds, logs]);

  /**
   * A vertical wheel over the strip scrolls it sideways.
   *
   * Without it the strip only scrolls by trackpad-swipe or the `N more ›` menu, and a mouse has
   * neither — it has a wheel, which did nothing at all over a region that visibly scrolls.
   *
   * Three conditions, each of which is a way to get this wrong:
   *
   * - **Only when there is something to scroll.** Otherwise a wheel over the strip swallows the
   *   page scroll for no benefit.
   * - **Only when the wheel is predominantly vertical.** A trackpad already sends `deltaX` for a
   *   sideways swipe, and adding `deltaY` on top of that makes the strip lurch at twice the speed.
   * - **`preventDefault` only when it actually consumed the event**, which is why this is
   *   registered with `addEventListener` and `passive: false`. React's `onWheel` is passive, so a
   *   `preventDefault` inside it is a no-op that logs a console warning and scrolls the page
   *   anyway.
   */
  useEffect(() => {
    const region = scrollRef.current;
    if (!region) return;
    const onWheel = (event: WheelEvent) => {
      if (region.scrollWidth <= region.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      region.scrollLeft += event.deltaY;
    };
    region.addEventListener("wheel", onWheel, { passive: false });
    return () => region.removeEventListener("wheel", onWheel);
  }, []);

  /**
   * Keep the active tab on screen.
   *
   * Without this, picking a tab out of the `N more` menu activates something you still cannot see —
   * which is the very complaint the scrolling strip had to answer. Layout effect, so it lands in
   * the same frame as the tab becoming active rather than a visible jump afterwards.
   */
  useLayoutEffect(() => {
    if (!activeLogId) return;
    tabRefs.current
      .get(activeLogId)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeLogId]);

  useEffect(() => {
    if (!overflowOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!overflowRef.current?.contains(event.target as Node)) setOverflowOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [overflowOpen]);

  const overflow = openLogIds.filter((id) => hidden.has(id));

  /**
   * A tab accepts two drops that mean opposite things, told apart by the transfer's type.
   *
   * A **sentence** lands *in* this log; a **tab** lands *beside* it. Reading the type rather than
   * guessing is what keeps the commonest gesture — dragging a mis-read line into another log —
   * working now that tabs are draggable themselves.
   */
  const dropHandlers = (logId: string) => ({
    onDragOver: (event: React.DragEvent) => {
      const types = event.dataTransfer.types;
      if (types.includes(TAB_DRAG_TYPE)) {
        event.preventDefault();
        setTabOver({
          id: logId,
          before: dropsBeforeX(
            event.clientX,
            (event.currentTarget as HTMLElement).getBoundingClientRect(),
          ),
        });
        return;
      }
      if (!onDropSentence || !types.includes(SENTENCE_DRAG_TYPE)) return;
      event.preventDefault();
      setDropTarget(logId);
    },
    onDragLeave: () => {
      setDropTarget(null);
      setTabOver((current) => (current?.id === logId ? null : current));
    },
    onDrop: (event: React.DragEvent) => {
      const movedTab = event.dataTransfer.getData(TAB_DRAG_TYPE);
      const before = dropsBeforeX(
        event.clientX,
        (event.currentTarget as HTMLElement).getBoundingClientRect(),
      );
      setDropTarget(null);
      setTabOver(null);
      if (movedTab) {
        event.preventDefault();
        onMoveTab(movedTab, logId, before);
        return;
      }
      const sentenceId = event.dataTransfer.getData(SENTENCE_DRAG_TYPE);
      if (!sentenceId || !onDropSentence) return;
      event.preventDefault();
      onDropSentence(logId, sentenceId);
    },
  });

  const menu = useMenu();

  /**
   * A tab's right-click (日本-318).
   *
   * Built only from what the strip already does: rename is the double click, close is the `×` with
   * its named/unnamed policy intact, and `Move to folder ▸` is the library's action reached from
   * here rather than a new one. That is why `folders` and `onFileLog` are props — the strip does
   * not own filing and does not learn to.
   */
  const tabMenuItems = (logId: string): MenuItemSpec[] => {
    const entry = logs.find((candidate) => candidate.id === logId);
    const unsaved = entry?.name === null;
    return [
      {
        kind: "item",
        id: "rename",
        label: unsaved ? "ログに名前を付ける" : "名前を変更",
        hint: "ダブルクリック",
        onSelect: () => setRenaming({ logId, text: entry?.name ?? "" }),
      },
      {
        kind: "submenu",
        id: "file",
        label: "フォルダへ移動",
        empty: "フォルダがありません",
        items: [
          ...folders.map((folder): MenuItemSpec => ({
            kind: "item",
            id: `folder-${folder.id}`,
            label: folder.name,
            onSelect: () => onFileLog(logId, folder.id),
          })),
          ...(entry?.folderId != null
            ? [
                {
                  kind: "item" as const,
                  id: "folder-none",
                  label: "（フォルダなし）",
                  onSelect: () => onFileLog(logId, null),
                },
              ]
            : []),
        ],
      },
      { kind: "separator", id: "sep" },
      {
        kind: "item",
        id: "close",
        // Named → the log is kept and reopens from the library. Unnamed → it is discarded, because
        // a closed one would sit in `Unfiled` for ever. Two different acts, so two different words.
        label: unsaved ? "ログを破棄…" : "タブを閉じる",
        danger: unsaved,
        onSelect: () => (unsaved ? onDiscard : onClose)(logId),
      },
      { kind: "item", id: "new", label: "新規ログ", onSelect: onNew },
    ];
  };

  const openTabMenu = (event: React.MouseEvent, logId: string) => {
    if (
      !shouldOpenContextMenu(event.nativeEvent, {
        selectionInTarget: hasSelectionIn(event.currentTarget),
      })
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // The strip arms the same 220ms deferred click the library does, and a right-click fires no
    // `click` to disarm it — so without this, selecting a tab and then right-clicking it would
    // switch tabs underneath the menu.
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    menu.openAt({ x: event.clientX, y: event.clientY }, tabMenuItems(logId));
  };

  const commitRename = () => {
    if (!renaming) return;
    onRename(renaming.logId, renaming.text.trim() || null);
    setRenaming(null);
  };

  return (
    <div
      className="flex h-[34px] shrink-0 items-end gap-[2px] border-b border-[color:var(--line-strong)] bg-[color:var(--tab-strip)] px-[9px] box-border"
      role="tablist"
      aria-label="読書ログ"
    >
      <button
        type="button"
        onClick={onOpenLibrary}
        title="書架 (L)"
        aria-label="書架を開く"
        className="mb-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--r-2)] bg-[color:var(--tab-hover)] font-jp text-[14px]/[1] text-ink-3 hover:text-ink"
      >
        冊
      </button>
      <span aria-hidden className="mb-[5px] h-4 w-px shrink-0 bg-[color:var(--tab-divider)]" />

      {/* The scrolling region. `min-w-0` is what lets it actually shrink inside the flex row —
          without it the tabs push `N more` off the end instead of scrolling.

          **`pb-px -mb-px` is what stops it scrolling VERTICALLY, and it is not cosmetic.**
          `overflow-x: auto` gives you `overflow-y: auto` whether you asked for it or not — CSS
          computes `visible` to `auto` on one axis whenever the other is not `visible` — so this box
          both scrolled and clipped on an axis nobody wrote. The active tab's own `mb-[-1px]` then
          put its border box 1px below this padding box, which cost two things at once: 1px of
          vertical scroll (`scrollTop` really reached 1, and `scrollIntoView({block:"nearest"})` on
          every tab change could move it), and the merge below being *clipped away*, so the active
          tab sat on the strip's rule instead of joining the surface under it.

          The padding grows this padding box to contain that border box exactly — nothing to scroll,
          nothing to clip — and the negative margin pulls this element back over the strip's bottom
          rule so everything else on the strip stays put. **The negative margin has to be on the
          scroll container; one inside it cannot escape the box that clips it.**

          `overflow-y: hidden` is the obvious one-word version and is wrong: it removes the scroll
          and keeps the clipping, freezing the broken merge for good. */}
      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 items-end gap-[2px] overflow-x-auto pb-px -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {openLogIds.map((logId) => {
          const entry = logs.find((candidate) => candidate.id === logId);
          const isActive = logId === activeLogId;
          const unsaved = entry?.name === null;
          const label = logLabel(logs, logId);
          const isRenaming = renaming?.logId === logId;

          return (
            <div
              key={logId}
              ref={(element) => {
                if (element) tabRefs.current.set(logId, element);
                else tabRefs.current.delete(logId);
              }}
              data-log-id={logId}
              // **The whole tab is the handle** — the 34px strip has no room for a gripper, and
              // "click and drag tabs" is what was asked for. Not while renaming, or dragging to
              // select text inside the field would start a tab drag instead.
              draggable={!isRenaming}
              onDragStart={(event) => {
                event.dataTransfer.setData(TAB_DRAG_TYPE, logId);
                event.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setTabOver(null)}
              role="tab"
              aria-selected={isActive}
              onContextMenu={(event) => openTabMenu(event, logId)}
              tabIndex={isActive ? 0 : -1}
              onClick={(event) => {
                if (isRenaming) return;
                // Deferred, and cancelled by the `onDoubleClick` below: without it a double click
                // to rename also selects on the way through. Harmless on a tab, but the library
                // rows need the same guard for a click that OPENS a log, so both use one rule.
                if (event.detail > 1) return;
                if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
                clickTimer.current = window.setTimeout(() => onSelect(logId), DOUBLE_CLICK_MS);
              }}
              onDoubleClick={() => {
                if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
                setRenaming({ logId, text: entry?.name ?? "" });
              }}
              onAuxClick={(event) => {
                if (event.button !== 1) return;
                // Chromium starts autoscroll on middle-click otherwise, leaving the page in a mode
                // the next click has to cancel. Middle-click stays as well as the `×`, not instead
                // of it — it is free, and it is the browser idiom for the same gesture.
                event.preventDefault();
                (unsaved ? onDiscard : onClose)(logId);
              }}
              title={
                `${label} — ${entry?.sentenceCount ?? 0}文。ダブルクリックで名前を変更。` +
                `${unsaved ? "閉じると破棄されます。" : "閉じてもログは残ります。"}`
              }
              {...dropHandlers(logId)}
              className={[
                "group flex shrink-0 cursor-default items-center gap-[7px] rounded-t-[var(--r-3)] px-[11px] box-border",
                isActive
                  ? // -1px and a bottom border painted in the log region's own colour: the active
                    // tab has to MERGE with the surface below it, not sit on a line above it. That
                    // colour is --tab-merge (--paper's value), never --surface — see tokens.css.
                    "mb-[-1px] h-[27px] border border-[color:var(--line-strong)] border-b-[color:var(--tab-merge)] bg-[color:var(--tab-merge)] text-[14px]/[1] font-semibold text-ink"
                  : "h-6 text-[14px]/[1] text-ink-3 hover:bg-[color:var(--tab-hover)] hover:text-ink-2",
                unsaved && !isRenaming ? "italic text-ink-4" : "",
                dropTarget === logId ? "bg-accent-tint" : "",
              ].join(" ")}
              style={{
                ...(unsaved && !isActive
                  ? { borderBottom: "1px dashed var(--line-strong)" }
                  : {}),
                // Where a dragged tab would land: the library's edge rule, turned ninety degrees.
                // Joined with the active tab's own borders rather than replacing them.
                ...(tabOver?.id === logId
                  ? {
                      boxShadow: tabOver.before
                        ? "inset 2px 0 0 var(--accent)"
                        : "inset -2px 0 0 var(--accent)",
                    }
                  : {}),
              }}
            >
              {/* The name reads FIRST. A 3px dot leads the active tab and the count follows dimmed
                  — you scan the strip for a title, not for a number. */}
              {isActive && !isRenaming && (
                <span
                  aria-hidden
                  className="h-[3px] w-[3px] shrink-0 rounded-full bg-[color:var(--accent)]"
                />
              )}

              {isRenaming ? (
                <input
                  autoFocus
                  // **Select what is there.** Double-click-to-rename lands on a field already
                  // holding the old name, so without this renaming 第三章 to 第四章 means typing
                  // and then backspacing over what you were replacing. Clicking once inside still
                  // puts the caret where you clicked, so nothing is lost.
                  onFocus={(event) => event.currentTarget.select()}
                  value={renaming.text}
                  placeholder="ログ名"
                  aria-label="ログ名"
                  // Sized to its content so the strip does not lurch while you type, with a floor
                  // so an empty field is still a target.
                  size={Math.max(renaming.text.length, 8)}
                  onChange={(event) => setRenaming({ logId, text: event.currentTarget.value })}
                  onBlur={commitRename}
                  onKeyDown={(event) => {
                    // preventDefault marks the key answered, so Read's window handler leaves the
                    // keys of a field it cannot see alone.
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitRename();
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      setRenaming(null);
                    }
                  }}
                  className="min-w-0 rounded-[var(--r-2)] border border-accent bg-panel px-[4px] text-[14px]/[1.4] text-ink outline-none"
                />
              ) : (
                <span className="max-w-[14ch] truncate">{label}</span>
              )}

              <span
                className={[
                  "shrink-0 font-mono text-[14px]/[1]",
                  isActive ? "text-ink-4" : "text-ink-4",
                ].join(" ")}
              >
                {entry?.sentenceCount ?? 0}
              </span>

              {!isRenaming && (
                <button
                  type="button"
                  // stopPropagation, or closing a tab also selects it on the way past — and for
                  // the tab you are discarding that means loading a log that is about to go.
                  onClick={(event) => {
                    event.stopPropagation();
                    (unsaved ? onDiscard : onClose)(logId);
                  }}
                  aria-label={unsaved ? `${label}を破棄` : `${label}を閉じる`}
                  title={unsaved ? "この未保存のログを破棄" : "タブを閉じる — ログは残ります"}
                  className={[
                    "-mr-[4px] shrink-0 rounded-[var(--r-2)] px-[3px] text-[14px]/[1] text-ink-4 hover:text-accent-text",
                    // Always on the active tab; on hover elsewhere. A `×` on every tab at once in
                    // a 34px band is a row of targets you did not ask for.
                    isActive
                      ? ""
                      : "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
                  ].join(" ")}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onToggleFind}
        data-check="read.find-toggle"
        aria-pressed={findOpen}
        title="ログ内を検索 (Ctrl+F)"
        aria-label="ログ内を検索"
        className={[
          "mb-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--r-2)] font-jp text-[14px]/[1]",
          findOpen
            ? "border border-[color:var(--accent-border)] bg-[color:var(--accent-tint-2)] text-accent-text"
            : "text-ink-3 hover:bg-[color:var(--tab-hover)] hover:text-ink",
        ].join(" ")}
      >
        検
      </button>

      <button
        type="button"
        onClick={onNew}
        data-check="read.new-log"
        title="新規ログ"
        aria-label="新規ログ"
        className="mb-[3px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[var(--r-2)] text-[16px]/[1] text-ink-3 hover:bg-[color:var(--tab-hover)] hover:text-ink"
      >
        +
      </button>

      {overflow.length > 0 && (
        <div ref={overflowRef} className="relative mb-[3px] shrink-0">
          <button
            type="button"
            onClick={() => setOverflowOpen((open) => !open)}
            aria-expanded={overflowOpen}
            title="表示範囲外のタブ"
            className="flex h-[22px] items-center gap-1 rounded-[var(--r-2)] px-[7px] text-[14px]/[1] text-ink-3 hover:bg-[color:var(--tab-hover)] hover:text-ink"
          >
            他{overflow.length}件 <span aria-hidden>›</span>
          </button>
          {overflowOpen && (
            <div style={{ zIndex: "var(--z-menu)", boxShadow: "var(--sh-popover)" }}
              className="absolute right-0 top-[24px] min-w-[180px] rounded-[var(--r-2)] border border-line bg-panel py-1">
              {overflow.map((logId) => (
                <button
                  key={logId}
                  type="button"
                  onClick={() => {
                    // Selecting is enough to bring it on screen — the layout effect above scrolls
                    // whatever becomes active into view.
                    onSelect(logId);
                    setOverflowOpen(false);
                  }}
                  {...dropHandlers(logId)}
                  className="flex w-full items-center gap-2 px-3 py-[5px] text-left text-[14px]/[1.5] text-ink-2 hover:bg-[color:var(--tab-hover)]"
                >
                  <span className="min-w-0 flex-1 truncate">{logLabel(logs, logId)}</span>
                  <span className="font-mono text-[14px] text-ink-4">
                    {logs.find((candidate) => candidate.id === logId)?.sentenceCount ?? 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
